"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { ApiException, api, type Me } from "./api";
import {
  clearTokens,
  getAccessToken,
  setTokens,
  subscribeTokens,
} from "./token";

// 관리자 콘솔은 최고관리자(super_admin) 등급만 접근. 트레이너·데스크·환자는 차단(담당자/환자 앱 사용).
const ADMIN_GRADE = "super_admin";
function isAdmin(me: Me | null): boolean {
  return me?.grade === ADMIN_GRADE;
}

type AuthContextValue = {
  token: string | null;
  me: Me | null;
  ready: boolean; // 초기 토큰 복원 완료 여부
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);

  // 토큰 스토어 변경(자동 갱신·만료)을 React 상태에 동기화. 만료 시 token=null → 로그인 유도.
  useEffect(() => {
    return subscribeTokens(() => {
      const next = getAccessToken();
      setToken(next);
      if (!next) setMe(null);
    });
  }, []);

  // 새로고침 시 저장된 토큰 복원 + 유효성 확인 (만료된 액세스 토큰은 api.me 호출 중 자동 갱신됨)
  useEffect(() => {
    let active = true;
    void (async () => {
      const saved = getAccessToken();
      if (!saved) {
        if (active) setReady(true);
        return;
      }
      try {
        const profile = await api.me(saved);
        if (!isAdmin(profile)) {
          clearTokens(); // 관리자 등급이 아닌 토큰 → 콘솔 접근 불가
          return;
        }
        if (active) {
          setToken(getAccessToken()); // 갱신됐을 수 있으니 스토어의 최신 토큰 채택
          setMe(profile);
        }
      } catch {
        clearTokens(); // 액세스·리프레시 모두 무효 → 세션 폐기
      } finally {
        if (active) setReady(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const tokens = await api.staffLogin(email, password);
    const profile = await api.me(tokens.accessToken); // 토큰 저장 전에 등급 확인
    if (!isAdmin(profile)) {
      clearTokens();
      throw new ApiException("FORBIDDEN", "관리자 권한이 없는 계정입니다. 담당자 앱을 이용해 주세요.", 403);
    }
    setTokens({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
    setToken(tokens.accessToken);
    setMe(profile);
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setToken(null);
    setMe(null);
  }, []);

  return (
    <AuthContext.Provider value={{ token, me, ready, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth 는 AuthProvider 내부에서만 사용할 수 있습니다.");
  }
  return ctx;
}
